import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import axios from 'axios';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code, Image as ImageIcon, Link as LinkIcon,
  AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3,
  Undo, Redo, Minus, Upload, Loader2, Variable, FileCode
} from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'h-8 w-8 p-0',
      isActive && 'bg-white/20 text-primary'
    )}
  >
    {children}
  </Button>
);

const Divider = () => (
  <div className="w-px h-6 bg-white/20 mx-1" />
);

const TipTapEditor = ({ 
  content = '', 
  onChange, 
  placeholder = 'Zacznij pisać...',
  className = '',
  uploadEndpoint = '/api/admin/blog/upload-image',
  variables = [],  // List of variables that can be inserted
  showSourceView = true  // Show HTML source toggle
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [sourceContent, setSourceContent] = useState(content);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Underline,
      TextStyle,
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg'
        }
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline'
        }
      }),
      Placeholder.configure({
        placeholder
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      })
    ],
    content,
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[200px] px-4 py-3'
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setSourceContent(html);
      onChange?.(html);
    }
  });

  // Update editor when content prop changes from outside
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
      setSourceContent(content);
    }
  }, [content, editor]);

  // Insert variable at cursor position
  const insertVariable = useCallback((variable) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${variable}}}`).run();
    setShowVariables(false);
  }, [editor]);

  // Toggle source view
  const toggleSourceView = useCallback(() => {
    if (showSource) {
      // Switching from source to visual - apply source changes
      editor?.commands.setContent(sourceContent, false);
      onChange?.(sourceContent);
    }
    setShowSource(!showSource);
  }, [showSource, sourceContent, editor, onChange]);

  // Handle source content change
  const handleSourceChange = useCallback((e) => {
    setSourceContent(e.target.value);
  }, []);

  // Apply source changes on blur
  const handleSourceBlur = useCallback(() => {
    editor?.commands.setContent(sourceContent, false);
    onChange?.(sourceContent);
  }, [sourceContent, editor, onChange]);

  // Upload image file
  const handleImageUpload = useCallback(async (file) => {
    if (!file || !editor) return;
    
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Musisz być zalogowany, aby wgrywać zdjęcia');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(
        `${API}${uploadEndpoint}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.url) {
        // Backend returns /api/uploads/... so we need to prepend API base
        const fullUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${API}${response.data.url}`;
        editor.chain().focus().setImage({ src: fullUrl }).run();
        toast.success('Zdjęcie wgrane!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Nie udało się wgrać zdjęcia');
    } finally {
      setUploading(false);
    }
  }, [editor, uploadEndpoint]);

  const addImage = useCallback(() => {
    // Show dialog with two options
    const choice = window.confirm(
      'Kliknij OK aby wybrać plik z komputera,\nlub Anuluj aby podać URL.'
    );
    
    if (choice) {
      // Open file picker
      fileInputRef.current?.click();
    } else {
      // URL prompt
      const url = window.prompt('URL obrazka:');
      if (url && editor) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    }
  }, [editor]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input
    e.target.value = '';
  }, [handleImageUpload]);

  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL linku:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn('border border-white/10 rounded-lg overflow-hidden', className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 bg-white/5 border-b border-white/10">
        {/* History */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Cofnij"
        >
          <Undo className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Ponów"
        >
          <Redo className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Headings */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Nagłówek 1"
        >
          <Heading1 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Nagłówek 2"
        >
          <Heading2 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Nagłówek 3"
        >
          <Heading3 className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Text formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Pogrubienie"
        >
          <Bold className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Kursywa"
        >
          <Italic className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Podkreślenie"
        >
          <UnderlineIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Przekreślenie"
        >
          <Strikethrough className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Wyrównaj do lewej"
        >
          <AlignLeft className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Wyśrodkuj"
        >
          <AlignCenter className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Wyrównaj do prawej"
        >
          <AlignRight className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Lista punktowana"
        >
          <List className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Lista numerowana"
        >
          <ListOrdered className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Block elements */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Cytat"
        >
          <Quote className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Blok kodu"
        >
          <Code className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Linia pozioma"
        >
          <Minus className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Media */}
        <MenuButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Dodaj link"
        >
          <LinkIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={addImage}
          disabled={uploading}
          title="Dodaj obrazek (z komputera lub URL)"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ImageIcon className="w-4 h-4" />
          )}
        </MenuButton>

        {/* Variables dropdown - only show if variables prop is provided */}
        {variables && variables.length > 0 && (
          <>
            <Divider />
            <div className="relative">
              <MenuButton
                onClick={() => setShowVariables(!showVariables)}
                isActive={showVariables}
                title="Wstaw zmienną"
              >
                <Variable className="w-4 h-4" />
              </MenuButton>
              
              {showVariables && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-[#1a1a2e] border border-white/20 rounded-lg shadow-xl p-2 min-w-[200px] max-h-[300px] overflow-y-auto">
                  <div className="text-xs text-gray-400 mb-2 px-2">Kliknij aby wstawić:</div>
                  {variables.map(v => (
                    <button
                      key={v}
                      onClick={() => insertVariable(v)}
                      className="w-full text-left px-2 py-1.5 text-sm hover:bg-white/10 rounded font-mono text-purple-400"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Source view toggle */}
        {showSourceView && (
          <>
            <Divider />
            <MenuButton
              onClick={toggleSourceView}
              isActive={showSource}
              title={showSource ? "Widok wizualny" : "Widok HTML"}
            >
              <FileCode className="w-4 h-4" />
            </MenuButton>
          </>
        )}
        
        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Editor content or Source view */}
      <div className="bg-white/5">
        {showSource ? (
          <textarea
            value={sourceContent}
            onChange={handleSourceChange}
            onBlur={handleSourceBlur}
            className="w-full min-h-[300px] p-4 bg-transparent text-gray-300 font-mono text-sm resize-y focus:outline-none focus:ring-1 focus:ring-primary/50"
            placeholder="<p>Wpisz HTML...</p>"
            spellCheck={false}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>

      {/* Styles */}
      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #6b7280;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror h3 {
          font-size: 1.25em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #8b5cf6;
          padding-left: 1em;
          margin: 1em 0;
          color: #9ca3af;
        }
        .ProseMirror code {
          background: rgba(255,255,255,0.1);
          padding: 0.2em 0.4em;
          border-radius: 4px;
          font-family: monospace;
        }
        .ProseMirror pre {
          background: rgba(0,0,0,0.3);
          padding: 1em;
          border-radius: 8px;
          overflow-x: auto;
        }
        .ProseMirror hr {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.2);
          margin: 1em 0;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default TipTapEditor;
